import { useEffect, useRef, type FC, type ReactNode } from 'react'
import { Icon } from './Icon.js'

export type ModalTone = 'plain' | 'ok' | 'warn' | 'bad'

export interface ModalProps {
  title: string
  /** For a dialog carrying a table: a narrow one wraps every label to death. */
  wide?: boolean
  subtitle?: string
  tone?: ModalTone
  /** Secondary text at the far left of the footer, e.g. "Line #1 of 3". */
  footerLeft?: ReactNode
  actions: ReactNode
  onClose: () => void
  children?: ReactNode
}

export const Modal: FC<ModalProps> = ({
  title,
  wide = false,
  subtitle,
  tone = 'plain',
  footerLeft,
  actions,
  onClose,
  children,
}) => {
  const dialog = useRef<HTMLDivElement | null>(null)
  // Read through a ref so a caller passing a fresh closure on every render does
  // not re-run the effect below — that re-ran the focus call on every keystroke
  // and pulled the caret out of the field being typed into.
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    // A dialog that asks for something focuses what it asks for; one that only
    // asks for a decision focuses itself, so the keyboard reaches its buttons.
    const asked = dialog.current?.querySelector<HTMLElement>('[autofocus], input, textarea, select')
    ;(asked ?? dialog.current)?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current()
    }
    document.addEventListener('keydown', onKey)

    // A dialog over a scrolled list must not let the list scroll behind it.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [])

  return (
    <div
      className="modal-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={dialog}
        className={wide ? `modal modal--${tone} modal--wide` : `modal modal--${tone}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className="modal__head">
          <div>
            <div className="modal__title">{title}</div>
            {subtitle ? <div className="modal__sub">{subtitle}</div> : null}
          </div>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            <Icon name="x" size={20} />
          </button>
        </div>

        <div className="modal__body">{children}</div>

        <div className="modal__foot">
          {footerLeft ? <div className="modal__foot-left">{footerLeft}</div> : null}
          <div className="modal__actions">{actions}</div>
        </div>
      </div>
    </div>
  )
}
